import {MetadataKey, Key, Query} from '../../backend/model';
import {PropertyFilterProps} from '@amzn/awsui-components-react'
import {PropertyFilterOperator, PropertyFilterToken} from '@amzn/awsui-collection-hooks'
import {INPUT_FIELD_PREFIX, OUTPUT_FIELD_PREFIX} from '../metadata/Constants';

export const SPECIAL_PURPOSED_QUERY_PROPERTY = [
    'view',
    'features',
    'operation',
    'page',
    `${INPUT_FIELD_PREFIX}operation`,
    `${OUTPUT_FIELD_PREFIX}operation`
]

export const constructAdvancedSearchQuery = (
    queries: { [key: string]: any },
    queryFilterHandler: (key: string) => Boolean = (key) => !SPECIAL_PURPOSED_QUERY_PROPERTY.includes(key),
    operationKeyPrefix: string = ""
): { [key: string]: string | string[] | undefined } => {
    const operationKey = `${operationKeyPrefix}operation`
    const advancedSearchQuery: { [key: string]: string | string[] | undefined } = {
        [operationKey]: queries[operationKey]?.trim() || undefined
    }

    for (const key in queries) {
        if (queryFilterHandler(key)) {
            const value = (queries[key] as string)?.trim() || undefined
            if (value) {
                advancedSearchQuery[key] = value.split(',')
            }
        }
    }
    return advancedSearchQuery
}

export const toAdvancedQueryString = (query: { [key: string]: string | string[] | undefined }) => {
    return Object.entries(query).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)).map(
        ([key, value]) => `${key}=${value}`,
    ).join(',')
}

export const convertToPropertyFilterQuery = (query?: Query, operationKeyPrefix: string = ""): PropertyFilterProps.Query => {
    if (!query) return {
        tokens: [],
        operation: 'and' as PropertyFilterProps.JoinOperation
    }
    const tokens: PropertyFilterProps.Token[] = []

    const operationKey = `${operationKeyPrefix}operation`

    const operation = query[operationKey]

    for (const searchKey in query) {
        if (searchKey == operationKey) continue
        const searchValues = query[searchKey]
        if (searchValues && searchValues.length > 0) {
            for (const value of searchValues) {
                tokens.push(parseQueryToToken(searchKey, value))
            }
        }
    }

    return {
        tokens,
        operation: operation as PropertyFilterProps.JoinOperation,
    }
}

export const convertToQuery = (propertyFilterQuery: PropertyFilterProps.Query, operationKeyPrefix: string = ""): Query => {
    const query: Query = {}
    query[`${operationKeyPrefix}operation`] = propertyFilterQuery.operation

    for (const token of propertyFilterQuery.tokens) {
        const {value, propertyKey, operator} = token

        const trimmedValue = value.trim()
        if (propertyKey) {
            const propertyValue = (operator === '!=' ? 'NE:' : '').concat(trimmedValue)
            if (!query[propertyKey]) {
                query[propertyKey] = []
            }

            (query[propertyKey] as string[]).push(propertyValue)
        }
    }

    return query
}

export const getPropertyFiltersFromOpenSearchKeys = (keys: Key[]): [
    PropertyFilterProps.FilteringProperty[],
    PropertyFilterProps.FilteringOption[]
] => {
    const filterProperties: PropertyFilterProps.FilteringProperty[] = []
    const filterOptions: PropertyFilterProps.FilteringOption[] = []

    keys.map(key => key.key).sort().forEach(key => {
        const label = convertOpenSearchKeyToLabel(key)
        filterProperties.push(
            {
                key: key,
                // TODO: Enable != when operator is supported in the backend.
                // operators: ['=', '!='],
                operators: ['='],
                propertyLabel: label,
                groupValuesLabel: `${label}s`,
            },
        )
    })
    return [filterProperties, filterOptions]
}

export const getPropertyFiltersFromMetadataKeys = (metadataKeys: MetadataKey[]): [
    PropertyFilterProps.FilteringProperty[],
    PropertyFilterProps.FilteringOption[]
] => {
    const filterProperties: PropertyFilterProps.FilteringProperty[] = []
    const filterOptions: PropertyFilterProps.FilteringOption[] = []

    for (const key of metadataKeys) {
        const label = convertKeyToLabel(key.key)
        filterProperties.push(
            {
                key: key.key,
                operators: ['=', '!='],
                propertyLabel: label,
                groupValuesLabel: `${label}s`,
            },
        )
        if (key.allowedValues) {
            key.allowedValues.sort().forEach(allowed => {
                filterOptions.push(
                    {
                        propertyKey: key.key,
                        value: allowed,
                    }
                )
            })
        }
    }
    return [filterProperties, filterOptions]
}

function parseQueryToToken(queryKey: string, queryValue: string): PropertyFilterToken {
    let operator: PropertyFilterOperator = '='
    let value = queryValue

    if (value.includes(':')) {
        const [keyword, extractedValue] = value.split(':')
        value = extractedValue
        operator = (keyword === 'NE') ? '!=' : '='
    }

    return {
        operator: operator,
        propertyKey: queryKey,
        value: value,
    }
}

export const convertOpenSearchKeyToLabel = (key: string): string => {
    const regExp = RegExp("_", "g")
    return key.split('-')[1].replace(regExp, ' ').toUpperCase()
}

export const convertKeyToLabel = (key: string): string => {
    return key.split('_').map((str) =>
        str.charAt(0).toUpperCase() + str.slice(1),
    ).join(' ')
}

export const convertQueryToString = (query?: Query) => {
    const resQuery: { [key: string]: string } = {}
    if (query) {
        for (const key in query) {
            const value = query[key]
            if (value) {
                if (Array.isArray(value) && value.length > 0) {
                    resQuery[key] = value.join(',')
                } else if (typeof value === 'string') {
                    resQuery[key] = value
                }
            }
        }
    }

    return resQuery
}
